import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Routes, Route, useParams, useNavigate } from "react-router-dom";
import { payrollApi, fmtCents, type PayrollRun } from "../lib/business";
import { downloadFile } from "../lib/api";

export function PayrollPage() {
  return (
    <Routes>
      <Route index element={<RunList />} />
      <Route path=":id" element={<RunDetail />} />
    </Routes>
  );
}

function RunList() {
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["payroll-runs"], queryFn: () => payrollApi.listRuns() });
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function newRun() {
    setBusy(true); setErr(null);
    try {
      await payrollApi.createRun(year, month);
      qc.invalidateQueries({ queryKey: ["payroll-runs"] });
      refetch();
    } catch (e: any) {
      setErr(e?.message ?? "Failed");
    } finally {
      setBusy(false);
    }
  }

  const monthName = (m: number) => new Date(2000, m - 1, 1).toLocaleString("en-LK", { month: "long" });

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Payroll</div>
          <h1 className="stage-title">Monthly <em>runs</em>.</h1>
          <p className="stage-sub">Each run computes EPF/ETF/APIT for every active employee, then posts a single journal.</p>
        </div>
      </div>

      <div className="card card-pad-lg" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Year</label>
            <select className="form-input" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[year + 1, year, year - 1].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Month</label>
            <select className="form-input" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" onClick={newRun} disabled={busy}>{busy ? "Creating…" : "+ New run"}</button>
        </div>
        {err && <div className="banner banner-error" style={{ marginTop: 10 }}>{err}</div>}
      </div>

      <div className="card card-pad-lg" style={{ padding: 0, overflow: "hidden" }}>
        <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Period</th>
              <th style={th}>Status</th>
              <th style={{ ...th, textAlign: "right" }}>Staff</th>
              <th style={{ ...th, textAlign: "right" }}>Gross</th>
              <th style={{ ...th, textAlign: "right" }}>Net</th>
              <th style={{ ...th, textAlign: "right" }}>APIT</th>
              <th style={{ ...th, textAlign: "right" }}>EPF</th>
              <th style={{ ...th, textAlign: "right" }}>ETF</th>
            </tr>
          </thead>
          <tbody>
            {!data?.runs.length && (
              <tr><td colSpan={8} style={{ padding: 60, textAlign: "center", color: "var(--ink-3)" }}>No runs yet.</td></tr>
            )}
            {data?.runs.map((r: PayrollRun) => (
              <tr key={r.id}>
                <td style={td}>
                  <Link to={`/payroll/${r.id}`} style={{ color: "var(--accent-deep, var(--ink))", fontWeight: 500 }}>
                    {monthName(r.month)} {r.year}
                  </Link>
                </td>
                <td style={td}><StatusBadge status={r.status} /></td>
                <td style={{ ...td, textAlign: "right" }}>{r.staff_count}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(r.total_gross_cents)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500 }}>{fmtCents(r.total_net_cents)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(r.total_apit_cents)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(BigInt(r.total_epf_employee_cents) + BigInt(r.total_epf_employer_cents))}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(r.total_etf_employer_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </>
  );
}

function RunDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, refetch } = useQuery({
    queryKey: ["payroll-run", id],
    queryFn: () => payrollApi.getRun(id!),
    enabled: !!id,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!data) return <div style={{ color: "var(--ink-3)" }}>Loading…</div>;

  async function compute() {
    setBusy(true); setErr(null);
    try {
      await payrollApi.computeRun(id!);
      qc.invalidateQueries({ queryKey: ["payroll-run", id] });
      qc.invalidateQueries({ queryKey: ["payroll-runs"] });
      refetch();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e.message);
    } finally { setBusy(false); }
  }

  async function post() {
    setBusy(true); setErr(null);
    try {
      await payrollApi.postRun(id!);
      qc.invalidateQueries({ queryKey: ["payroll-run", id] });
      qc.invalidateQueries({ queryKey: ["payroll-runs"] });
      refetch();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e.message);
    } finally { setBusy(false); }
  }

  const monthName = (m: number) => new Date(2000, m - 1, 1).toLocaleString("en-LK", { month: "long" });

  return (
    <>
      <button className="btn btn-ghost" onClick={() => navigate("/payroll")} style={{ marginBottom: 14 }}>← Back to runs</button>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Payroll run</div>
          <h1 className="stage-title">{monthName(data.month)} <em>{data.year}</em></h1>
          <div style={{ marginTop: 6 }}><StatusBadge status={data.status} /></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {data.status === "DRAFT" || data.status === "COMPUTED" ? (
            <button className="btn btn-line" onClick={compute} disabled={busy}>{busy ? "…" : "Compute"}</button>
          ) : null}
          {(data.status === "COMPUTED" || data.status === "POSTED") && (
            <button
              className="btn btn-line"
              onClick={() => downloadFile(`/payroll/runs/${id}/bank-file`, `payroll-bank-file-${data.year}-${String(data.month).padStart(2,"0")}.csv`)}
            >
              Bank file (CSV)
            </button>
          )}
          {data.status === "COMPUTED" && (
            <button className="btn btn-primary" onClick={post} disabled={busy}>{busy ? "…" : "Post to ledger"}</button>
          )}
        </div>
      </div>

      {err && <div className="banner banner-error" style={{ marginBottom: 14 }}>{err}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 18 }}>
        <Tile label="Staff" value={String(data.staff_count)} />
        <Tile label="Gross" value={fmtCents(data.totals.gross_cents)} />
        <Tile label="Net" value={fmtCents(data.totals.net_cents)} featured />
        <Tile label="APIT" value={fmtCents(data.totals.apit_cents)} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 18 }}>
        <Tile label="EPF (employee 8%)" value={fmtCents(data.totals.epf_employee_cents)} />
        <Tile label="EPF (employer 12%)" value={fmtCents(data.totals.epf_employer_cents)} />
        <Tile label="ETF (3%)" value={fmtCents(data.totals.etf_employer_cents)} />
      </div>

      <div className="card card-pad-lg" style={{ padding: 0, overflow: "hidden" }}>
        <div className="table-scroll"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th}>Emp #</th>
              <th style={th}>Name</th>
              <th style={{ ...th, textAlign: "right" }}>Gross</th>
              <th style={{ ...th, textAlign: "right" }}>NCB</th>
              <th style={{ ...th, textAlign: "right" }}>EPF EE</th>
              <th style={{ ...th, textAlign: "right" }}>EPF ER</th>
              <th style={{ ...th, textAlign: "right" }}>ETF</th>
              <th style={{ ...th, textAlign: "right" }}>APIT</th>
              <th style={{ ...th, textAlign: "right" }}>Net</th>
              <th style={{ ...th, textAlign: "right", width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {data.entries.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 60, textAlign: "center", color: "var(--ink-3)" }}>No entries yet — click Compute.</td></tr>
            )}
            {data.entries.map((e) => (
              <tr key={e.id}>
                <td style={td}>{e.emp_no}</td>
                <td style={td}><strong>{e.employee_name}</strong></td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(e.gross_cents)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", color: BigInt(e.ncb_cents) > 0n ? "var(--accent-deep, var(--ink))" : "var(--ink-3)" }}>{fmtCents(e.ncb_cents)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(e.epf_employee_cents)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(e.epf_employer_cents)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>{fmtCents(e.etf_employer_cents)}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }}>
                  {fmtCents(e.apit_cents)}
                  {e.apit_provisional && <span title="Tax table fell back to 01" style={{ marginLeft: 6, color: "var(--ink-3)", fontSize: 10 }}>~</span>}
                </td>
                <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)", fontWeight: 500 }}>{fmtCents(e.net_pay_cents)}</td>
                <td style={{ ...td, textAlign: "right" }}>
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: 11, padding: "4px 8px" }}
                    onClick={() => downloadFile(`/payroll/runs/${id}/payslips/${e.employee_id}`, `payslip-${e.emp_no}-${data.year}-${String(data.month).padStart(2,"0")}.pdf`)}
                  >
                    Payslip
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; fg: string }> = {
    DRAFT: { bg: "var(--paper-2)", fg: "var(--ink-3)" },
    COMPUTED: { bg: "rgba(60, 130, 230, 0.1)", fg: "#1d6fdb" },
    POSTED: { bg: "rgba(40, 170, 90, 0.1)", fg: "#188545" },
  };
  const s = map[status] ?? map.DRAFT;
  return (
    <span style={{ background: s.bg, color: s.fg, padding: "3px 10px", borderRadius: 100, fontSize: 11, fontWeight: 500, letterSpacing: ".05em" }}>
      {status}
    </span>
  );
}

function Tile({ label, value, featured }: { label: string; value: string; featured?: boolean }) {
  return (
    <div style={{
      padding: 18, borderRadius: 12,
      background: featured ? "var(--ink)" : "var(--paper-2)",
      color: featured ? "var(--paper)" : "var(--ink)",
    }}>
      <div style={{ fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase", opacity: 0.7 }}>{label}</div>
      <div style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 500, marginTop: 4 }}>{value}</div>
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: "left", padding: "12px 16px", fontSize: 11, letterSpacing: "0.1em",
  textTransform: "uppercase", color: "var(--ink-4)", fontWeight: 500,
  background: "var(--paper)", borderBottom: "0.5px solid var(--line)",
};
const td: React.CSSProperties = { padding: "12px 16px", borderBottom: "0.5px solid var(--line)" };
